'use client';

import { c as _c } from "react/compiler-runtime";
import type { Client, Hex, TransactionReceipt } from 'viem';
import { useMandatoryContext } from '@repo/hooks';
import type { ILogLayer } from '@repo/observability/client-logger';
import type { ErrorReporter } from '@repo/observability/errors';
import type { EventTracker } from '@repo/observability/events';
import { delay, ensureError } from '@repo/utils';
import { type PropsWithChildren, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { waitForTransactionReceipt } from 'viem/actions';
import { classifyTransactionError } from '../../utils';
import { walletManagesGas } from '../../utils/wallet-capabilities.utils';
import { ActionFlowContext } from './ActionFlowContext';
import { useActionFlowWagmiState } from './hooks/useActionFlowWagmiState';
import { useActionFlowWalletMetadata } from './hooks/useActionFlowWalletMetadata';
import { ACTION_FLOW_EVENTS, type ActionFlow, ActionFlowError, type ActionFlowGasPolicy, type ActionFlowReceipt, type ActionFlowStepSettlement, BatchTxBundleError, type Step } from './types.action-flow';
import { actionFlowReducer } from './utils/actionFlowReducer';
import { assertExpectedAccount } from './utils/assertExpectedAccount';
import { buildActionFlowErrorMetadata, type ActionFlowErrorMetadata, type BuildActionFlowErrorMetadataParams } from './utils/buildActionFlowErrorMetadata';
import { buildCallDetails } from './utils/buildCallDetails';
import { buildSteps } from './utils/buildSteps';
import { decodeRevertedStepErrorName } from './utils/decodeRevertedStep';
import { executeStep } from './utils/executeStep';
import { extractErrorMessage } from './utils/extractErrorMessage';
import { raceAbort } from './utils/raceAbort';
import { type AttributionIssueReporter, resolveAttributionOriginTag } from './utils/resolveAttribution';
import { runWaitCallback } from './utils/runWaitCallback';
import { getStepAfter, getStepBefore } from './utils/stepCallbacks';

// Default delay between sequential transaction steps to allow wallet RPC state propagation.
// Prevents wallet simulation failures (e.g., Rabby showing "Simulation Failed")
// when the wallet simulates the next tx before its RPC node has updated state
// from the previous confirmed tx. Apps can override it via the provider prop.
//
// Skipped whenever the prior call declared an explicit `after` callback: the
// callback has already bounded the gap with a predicate appropriate to the
// boundary, so adding the blanket delay on top is excess. Per ADR-2026-05-07
// Phase 4, this constant is removed once every flow that relied on the blanket
// delay has been migrated to an explicit `before` / `after` predicate.
const DEFAULT_INTER_TRANSACTION_DELAY_MS = 750;

/**
 * Structured log event names for full lifecycle visibility (developer debugging).
 * On-chain reverts use `COMPLETED` with `status: 'reverted'`; execution failures use `ERROR`.
 */
const ACTION_FLOW_LOG_EVENTS = {
  INITIALIZED: 'action_flow.initialized',
  STARTED: 'action_flow.started',
  STEP_STARTED: 'action_flow.step_started',
  TX_PENDING: 'action_flow.tx_pending',
  TX_RESULT: 'action_flow.tx_result',
  SIGNATURE_COMPLETED: 'action_flow.signature_completed',
  STEP_COMPLETED: 'action_flow.step_completed',
  COMPLETED: 'action_flow.completed',
  ERROR: 'action_flow.error',
  USER_REJECTED: 'action_flow.user_rejected'
} as const;
export type UseActionFlowResponse = ReturnType<typeof useActionFlowHook>;

/** Per-execution identity: the abort controller (also the mutex) and the submissionId share a lifecycle. */
type ExecuteState = {
  controller: AbortController;
  submissionId: string;
};
type ActionFlowWalletMetadata = ReturnType<typeof useActionFlowWalletMetadata>;
function useActionFlowHook({
  errorReporter,
  eventTracker,
  logger,
  onComplete,
  onStepSettled,
  interTransactionDelayMs,
  gasPolicy,
  trackingContextRef,
  walletMetadata,
  baseTrackingContext
}: {
  errorReporter: ErrorReporter | undefined;
  eventTracker: EventTracker | undefined;
  logger: ILogLayer | undefined;
  onComplete: (() => void | Promise<void>) | undefined;
  onStepSettled: ((settlement: ActionFlowStepSettlement) => void) | undefined;
  interTransactionDelayMs: number;
  gasPolicy: ActionFlowGasPolicy | undefined;
  trackingContextRef: React.RefObject<Record<string, unknown>>;
  walletMetadata: ActionFlowWalletMetadata;
  baseTrackingContext: Record<string, unknown> | undefined;
}) {
  const [actionFlow, setActionFlow] = useState<ActionFlow | null>(null);
  const [state, dispatch] = useReducer(actionFlowReducer, {
    flowState: 'idle',
    stepIndex: 0
  });
  // Surfaced to the dialog so it can swap copy to "Waiting…" while a
  // `before` / `after` callback is awaiting. Lives outside the reducer
  // because ADR-2026-05-07 mandates the reducer stay unchanged — `before`
  // folds into `pending-wallet`, `after` folds into `finalizing`, and this
  // flag is the orthogonal "a callback is currently running" channel the
  // ADR calls for.
  const [isCallbackInFlight, setIsCallbackInFlight] = useState(false);
  const isIdle = state.flowState === 'idle';
  const isRetryBlocked = state.flowState === 'idle' && !!state.isRetryBlocked;
  const isExecution = state.flowState === 'execution';
  const isSuccess = state.flowState === 'success';
  const isReverted = state.flowState === 'reverted';
  const txHash = state.flowState === 'success' || state.flowState === 'reverted' ? state.txHash : null;

  // Abort controller (also the mutex/lock preventing simultaneous executions) bundled
  // with the submissionId so the whole per-execution identity is set and cleared together.
  const executeRef = useRef<ExecuteState | null>(null);
  const {
    walletClient,
    publicClient,
    walletSupportsAtomicBatching,
    ensureCorrectChain,
    isLoading
  } = useActionFlowWagmiState(actionFlow?.chainId ?? 1, {
    isFlowPrepared: !!actionFlow && actionFlow.callRequests.length > 0
  });
  const steps: readonly Step[] = useMemo(() => {
    if (!actionFlow) return [];
    const {
      label,
      signatureRequests,
      callRequests
    } = actionFlow;
    return buildSteps({
      flowLabel: label,
      shouldBatch: walletSupportsAtomicBatching,
      signatureRequests,
      callRequests
    });
  }, [actionFlow, walletSupportsAtomicBatching]);
  const hasSteps = steps.length > 0;

  /** Emit an event with tracking context merged in. */
  const track = useCallback((event: string, properties?: Record<string, unknown>) => {
    eventTracker?.track(event, {
      ...walletMetadata,
      ...trackingContextRef.current,
      ...(executeRef.current && {
        submissionId: executeRef.current.submissionId
      }),
      ...(actionFlow?.tag && {
        flowTag: actionFlow.tag
      }),
      ...properties
    });
  }, [actionFlow?.tag, eventTracker, trackingContextRef, walletMetadata]);
  const reportRenderError = useCallback((error: unknown, info: {
    componentStack?: string | null;
  }) => {
    errorReporter?.captureException(error instanceof Error ? error : new Error(String(error)), {
      errorSource: 'action_flow_ui',
      flowLabel: actionFlow?.label,
      flowTag: actionFlow?.tag,
      chainId: actionFlow?.chainId,
      componentStack: info.componentStack
    });
  }, [actionFlow?.chainId, actionFlow?.label, actionFlow?.tag, errorReporter]);

  // Runtime attribution skips (a tagged call whose calldata can't safely carry
  // the suffix) are never fatal — attribution is optional and must not block a
  // flow. (Invalid origin tags don't reach here: `resolveAttributionOriginTag`
  // throws on those in dev/test.) These are self-detected guards, not caught
  // exceptions, so per CONVENTIONS.md ("Only capture thrown errors") we log a
  // message + metadata rather than a synthetic `new Error` (no useful callstack,
  // just Sentry noise); the logger's error-reporter plugin still routes it to
  // Sentry + BetterStack, and the event lets analytics count the coverage gap.
  // Falls back to console.warn when no logger is wired (tests / local dev).
  const reportAttributionIssue: AttributionIssueReporter = useCallback((reason, meta) => {
    const context = {
      reason,
      flowLabel: actionFlow?.label,
      flowTag: actionFlow?.tag,
      chainId: actionFlow?.chainId,
      ...meta
    };
    if (logger) {
      logger.withMetadata(context).error(`[action-flow attribution] ${reason}`);
    } else {
      console.warn(`[action-flow attribution] ${reason}`, context);
    }
    track(ACTION_FLOW_EVENTS.ATTRIBUTION_SKIPPED, context);
  }, [logger, track, actionFlow]);

  // Execute the action flow
  const execute = useCallback(async () => {
    if (!walletClient || !publicClient || !actionFlow) return;
    if (executeRef.current || state.flowState !== 'idle' || isRetryBlocked || isLoading) return;
    const controller = new AbortController();
    const submissionId = crypto.randomUUID();
    const executeState: ExecuteState = {
      controller,
      submissionId
    };
    executeRef.current = executeState;

    /**
     * Every dispatch after the initial `START_EXECUTION` goes through here. Reducer transitions are
     * unconditional, and an aborted execution keeps running until its awaited callbacks settle —
     * `reset()` releases the mutex immediately, so by then a newer execution may own the state.
     * Dropping the transition leaves the newer flow alone; reporting and tracking still run.
     */
    const dispatchUnlessAborted: typeof dispatch = action => {
      if (!controller.signal.aborted) dispatch(action);
    };

    /**
     * `isCallbackInFlight` is shared provider state, and an aborted execution's callback keeps
     * running until it settles — its cleanup would otherwise report "no callback running" for the
     * execution that replaced it, and consumers gating on the flag would treat the newer wait as
     * already finished. `reset()` clears the flag itself, so suppressing writes here cannot leave
     * it stuck on.
     */
    const setIsCallbackInFlightUnlessAborted = (value: boolean) => {
      if (!controller.signal.aborted) setIsCallbackInFlight(value);
    };

    /**
     * The tracking context this execution was submitted with. Consumers set it while building the
     * flow, before `execute`, so it is complete here — and pinning it means an abandoned
     * execution's late events keep their own order/market context instead of borrowing the context
     * a newer submission has since written to the mutable ref.
     */
    const executionTrackingContext = {
      ...trackingContextRef.current
    };
    const stepTypes = steps.map(step => step.type);
    const startingStepIndex = state.stepIndex;

    /**
     * Every event this execution emits, pinned to its own submission. `track` reads the
     * submissionId and the tracking context off mutable provider state, which `reset()` releases
     * while an abandoned execution's awaited callbacks are still settling — by then both are empty
     * or owned by a newer submission, and the late event would land on that one instead.
     * It also pins the wallet's batch capability, the whole flow's planned step types, and the
     * step this execution starts from.
     */
    const trackExecution: typeof track = (event_0, properties_0) => track(event_0, {
      ...executionTrackingContext,
      walletSupportsAtomicBatching,
      stepTypes,
      startingStepIndex,
      ...properties_0,
      submissionId
    });

    // Reportable execution errors use the child logger when available. The
    // logger's error-reporter plugin captures the exception from that same
    // structured log call; direct capture is only the no-logger fallback.
    const flowLogger = logger?.child().withContext({
      flowLabel: actionFlow.label,
      flowTag: actionFlow.tag,
      chainId: actionFlow.chainId,
      submissionId,
      userAddress: walletClient.account.address,
      stepCount: steps.length,
      ...walletMetadata,
      ...executionTrackingContext
    });
    flowLogger?.withMetadata({
      totalSteps: steps.length,
      startingStepIndex: state.stepIndex
    }).info(ACTION_FLOW_LOG_EVENTS.INITIALIZED);
    dispatch({
      type: 'START_EXECUTION'
    });
    flowLogger?.withMetadata({
      totalSteps: steps.length,
      startingStepIndex: state.stepIndex
    }).info(ACTION_FLOW_LOG_EVENTS.STARTED);
    trackExecution(ACTION_FLOW_EVENTS.STARTED, {
      flowLabel: actionFlow?.label,
      chainId: actionFlow?.chainId,
      totalSteps: steps.length
    });
    const remainingSteps = steps.slice(state.stepIndex);
    let activeStepIndex = state.stepIndex;
    let activeStep: Step | undefined = remainingSteps[0];
    let hasSubmittedActiveStep = false;
    // Lets error consumers tell a failed step from an `onSuccess` throw after every step confirmed.
    let phase: 'step' | 'on-success' = 'step';
    try {
      // Resolve attribution once per execution: validate the opt-in origin tag.
      // A misconfigured tag throws in dev (surfacing when the flow is exercised
      // locally) and is dropped silently in production so the flow goes out
      // untagged rather than blocked. This runtime throw is a dev safety net, not
      // a CI gate — the build-time guard for a bad tag is the app's env-boundary
      // check (`isValidOriginTag`). Kept inside the try (after executeRef is set)
      // so a dev-time throw still hits the catch/finally and releases the
      // execution mutex rather than stranding the flow locked in 'execution'.
      const originTag = resolveAttributionOriginTag(actionFlow.attribution);
      assertExpectedAccount({
        expectedAccountAddress: actionFlow.expectedAccountAddress,
        signerAddress: walletClient.account.address
      });
      await raceAbort(ensureCorrectChain(), controller.signal);

      // Execute steps
      let lastHash: Hex | null = null;
      let lastStatus: 'success' | 'reverted' = 'success';
      let lastReceipt: ActionFlowReceipt | null = null;
      let revertedErrorName: string | undefined;
      let revert: {
        error: Error;
        metadata: ActionFlowErrorMetadata;
      } | undefined;
      // `before` callbacks receive the prior call's receipt (or null when the
      // current step is the first call of the flow). Track the most recent
      // viem receipt across iterations so the predicate can read its block
      // number, hash, etc.
      let priorReceipt: TransactionReceipt | null = null;
      for (const step_0 of remainingSteps) {
        hasSubmittedActiveStep = false;
        if (controller.signal.aborted) {
          // Prevent additional user requests after aborted
          // It's difficult to actually abort wallet actions (it will still appear for the user since no standard for this)
          throw new ActionFlowError('Execution aborted', {
            reportable: false
          });
        }
        const currentStepIndex = state.stepIndex + remainingSteps.indexOf(step_0);
        activeStepIndex = currentStepIndex;
        activeStep = step_0;
        flowLogger?.withMetadata({
          stepIndex: currentStepIndex,
          stepLabel: step_0.label,
          stepType: step_0.type
        }).info(ACTION_FLOW_LOG_EVENTS.STEP_STARTED);

        // Resolve all of this iteration's callback lookups up-front so the
        // receipt-fetch gate, the `before` invocation, and the `after`
        // invocation can share them without redundant `getStep*` calls.
        const beforeCallback = getStepBefore(step_0);
        const afterCallback = getStepAfter(step_0);
        const nextStep = steps[currentStepIndex + 1];
        const nextBeforeCallback = nextStep ? getStepBefore(nextStep) : undefined;

        // Authored `before` predicate. Fires before the wallet popup; receives
        // the prior step's receipt (or null when this is the first call). The
        // reducer is already in `pending-wallet` here (from START_EXECUTION
        // for the first call, or from the prior step's COMPLETE_STEP) — per
        // ADR-2026-05-07 §UI articulation, `before` folds into `pending-wallet`.
        if (beforeCallback) {
          await runWaitCallback({
            phase: 'before',
            callback: beforeCallback,
            receipt: priorReceipt,
            publicClient,
            chainId: actionFlow.chainId,
            signal: controller.signal,
            setIsCallbackInFlight: setIsCallbackInFlightUnlessAborted,
            flowLogger,
            track: trackExecution,
            flowLabel: actionFlow.label,
            stepLabel: step_0.label,
            stepIndex: currentStepIndex
          });
        }

        // `before` can settle after an abort; the chain check would still reach the wallet.
        if (controller.signal.aborted) {
          throw new ActionFlowError('Execution aborted', {
            reportable: false
          });
        }
        if (step_0.type === 'singleTx' || step_0.type === 'batchTx') {
          await raceAbort(ensureCorrectChain(), controller.signal);
        }
        const stepCallDetails = buildCallDetails(step_0);
        const resp = await executeStep({
          walletClient,
          publicClient,
          step: step_0,
          callDetails: stepCallDetails,
          isSafeWallet: walletMetadata.isSafeWallet,
          gasPolicy,
          walletManagesGas: walletManagesGas({
            connectorId: walletMetadata.connectorId,
            isSafeWallet: walletMetadata.isSafeWallet
          }),
          signal: controller.signal,
          originTag,
          onAttributionIssue: reportAttributionIssue,
          logger: flowLogger?.child().withContext({
            stepIndex: currentStepIndex,
            stepLabel: step_0.label
          }),
          onTxPending: ({
            hash,
            bundleId,
            safeTxHash
          }) => {
            if (hash !== undefined || bundleId !== undefined || safeTxHash !== undefined) {
              hasSubmittedActiveStep = true;
            }
            dispatchUnlessAborted({
              type: 'PENDING_TX',
              txHash: hash,
              safeTxHash
            });
            flowLogger?.withMetadata({
              stepIndex: currentStepIndex,
              txHash: hash,
              bundleId,
              safeTxHash
            }).info(ACTION_FLOW_LOG_EVENTS.TX_PENDING);
          }
        });
        if (resp.type === 'signature') {
          flowLogger?.withMetadata({
            stepIndex: currentStepIndex
          }).info(ACTION_FLOW_LOG_EVENTS.SIGNATURE_COMPLETED);
        }
        if (resp.type === 'tx') {
          lastHash = resp.hash;
          lastStatus = resp.status;
          lastReceipt = resp.receipt;
          // Only fetch the full viem receipt if a callback downstream of this
          // step is actually going to consume it: either this step's own
          // `after`, or the next step's `before`. For flows that declare
          // neither (every action flow in the repo today except the wizard's
          // deploy → identity multicall), skip the publicClient round-trip
          // entirely.
          priorReceipt = afterCallback || nextBeforeCallback ? await resolveViemReceipt(publicClient, resp.hash, controller.signal) : null;
          if (resp.status === 'reverted') {
            // Decode before reporting so the decoded custom-error name enriches the
            // event (grouping/filtering by cause) instead of collapsing into one
            // generic "Transaction reverted". Best-effort: the decode fails open to
            // `undefined` and is wall-clock bounded, so the report is not held up.
            revertedErrorName = await decodeRevertedStepErrorName({
              step: step_0,
              callDetails: stepCallDetails,
              publicClient,
              account: walletClient.account.address,
              blockNumber: resp.receipt.blockNumber,
              signal: controller.signal
            });
            flowLogger?.withMetadata({
              stepIndex: currentStepIndex,
              txHash: resp.hash,
              status: resp.status,
              ...(revertedErrorName && {
                revertErrorName: revertedErrorName
              })
            }).warn(ACTION_FLOW_LOG_EVENTS.TX_RESULT);
            const error_1 = new Error(`Transaction reverted${revertedErrorName ? `: ${revertedErrorName}` : ''}`);
            const revertMetadata = {
              submissionId,
              walletMetadata,
              trackingContext: executionTrackingContext,
              walletAddress: walletClient.account.address,
              flowLabel: actionFlow?.label,
              flowTag: actionFlow?.tag,
              chainId: actionFlow?.chainId,
              flowState: state.flowState,
              step: step_0,
              stepIndex: currentStepIndex,
              error: error_1,
              errorType: 'transaction-revert',
              errorCategory: 'transaction_reverted_onchain',
              txHash: resp.hash,
              ...(revertedErrorName && {
                revertErrorName: revertedErrorName
              })
            } satisfies BuildActionFlowErrorMetadataParams;
            revert = {
              error: error_1,
              metadata: buildActionFlowErrorMetadata(revertMetadata)
            };
            onStepSettled?.({
              submissionId,
              stepIndex: currentStepIndex,
              status: 'reverted',
              receipt: resp.receipt,
              gas: resp.gas,
              error: error_1
            });
            // If tx reverted, stop the flow
            break;
          }
          onStepSettled?.({
            submissionId,
            stepIndex: currentStepIndex,
            status: 'success',
            receipt: resp.receipt,
            gas: resp.gas
          });
          flowLogger?.withMetadata({
            stepIndex: currentStepIndex,
            txHash: resp.hash,
            status: resp.status
          }).info(ACTION_FLOW_LOG_EVENTS.TX_RESULT);
        }

        // Authored `after` predicate. Fires once the receipt is in hand,
        // before the engine advances to the next step. The viem receipt is
        // forwarded as `ctx.receipt`. Per ADR-2026-05-07 §UI articulation,
        // `after` folds into `finalizing` — so dispatch FINALIZING up-front
        // and let the next iteration's COMPLETE_STEP transition back to
        // `pending-wallet` (or the post-loop dispatch advance to SUCCESS).
        if (afterCallback) {
          dispatchUnlessAborted({
            type: 'FINALIZING'
          });
          await runWaitCallback({
            phase: 'after',
            callback: afterCallback,
            receipt: priorReceipt,
            publicClient,
            chainId: actionFlow.chainId,
            signal: controller.signal,
            setIsCallbackInFlight: setIsCallbackInFlightUnlessAborted,
            flowLogger,
            track: trackExecution,
            flowLabel: actionFlow.label,
            stepLabel: step_0.label,
            stepIndex: currentStepIndex
          });
          // An `after` callback can await for as long as it likes, so reset() / unmount may have
          // landed while it ran. Stop here rather than advancing the step.
          if (controller.signal.aborted) return;
        }

        // Allow wallet RPC nodes time to propagate state between transactions.
        // Delay before COMPLETE_STEP so the UI stays on 'pending-transaction'
        // for the current step rather than jumping to 'pending-wallet' for the next.
        // Skipped whenever the boundary is already owned by an explicit
        // callback — either this step's honored `after` predicate, or the
        // next step's `before` predicate (legal per `assertNoDoubleWait`;
        // having one side is enough to own the boundary). Pre-PR every
        // boundary used this delay; the migration is to declared predicates.
        const isTransactionStep = step_0.type === 'singleTx' || step_0.type === 'batchTx';
        const isLastStep = currentStepIndex === steps.length - 1;
        const afterCallbackHonored = afterCallback && priorReceipt !== null;
        const boundaryHandledByCallback = afterCallbackHonored || Boolean(nextBeforeCallback);

        // Fallback breadcrumb: an `after` callback ran with `ctx.receipt =
        // null` because publicClient never returned the receipt the wallet
        // already confirmed. A documented fallback path per the
        // `WaitContext.receipt` TSDoc, not a contract violation, and the
        // boundary is still re-bounded by the next step's `before` or by
        // the configured inter-transaction delay below. `resolveViemReceipt` only nulls
        // after viem's own timeout, so a hit means severe RPC lag — worth a
        // warn. Aborted fetches (reset / unmount) are reset noise, not
        // degradation, so they stay silent.
        if (afterCallback && priorReceipt === null && !controller.signal.aborted) {
          flowLogger?.withMetadata({
            stepIndex: currentStepIndex,
            stepLabel: step_0.label
          }).warn('action_flow.after_callback_skipped_null_receipt');
        }
        if (isTransactionStep && !isLastStep && !boundaryHandledByCallback) {
          await delay(interTransactionDelayMs);
          // If reset() was called during the delay, exit silently —
          // reset() already dispatched RESET and cleaned up state.
          if (controller.signal.aborted) return;
        }
        dispatchUnlessAborted({
          type: 'COMPLETE_STEP'
        });
        flowLogger?.withMetadata({
          stepIndex: currentStepIndex,
          stepType: step_0.type
        }).info(ACTION_FLOW_LOG_EVENTS.STEP_COMPLETED);
      }
      if (lastStatus === 'success' && lastHash && lastReceipt) {
        dispatchUnlessAborted({
          type: 'FINALIZING'
        });
        phase = 'on-success';
        await actionFlow.onSuccess?.({
          hash: lastHash,
          receipt: lastReceipt,
          chainId: actionFlow.chainId
        });
        // `onSuccess` may await work of its own (indexer sync, refetches), which makes this a
        // window the user can reset the flow through. Terminal dispatches are unconditional in
        // the reducer, so without this the late SUCCESS would overwrite reset()'s RESET and
        // leave a closed flow parked in a stale terminal state.
        if (controller.signal.aborted) return;
      }
      if (lastStatus === 'success') {
        dispatchUnlessAborted({
          type: 'SUCCESS',
          txHash: lastHash
        });
      } else {
        dispatchUnlessAborted({
          type: 'REVERT',
          txHash: lastHash,
          errorName: revertedErrorName
        });
      }
      if (lastStatus === 'reverted' && revert) {
        reportActionFlowError({
          flowLogger,
          errorReporter,
          error: revert.error,
          metadata: {
            ...revert.metadata,
            status: 'reverted'
          },
          severity: 'error',
          event: ACTION_FLOW_LOG_EVENTS.COMPLETED
        });
      } else {
        flowLogger?.withMetadata({
          status: lastStatus,
          txHash: lastHash
        }).info(ACTION_FLOW_LOG_EVENTS.COMPLETED);
      }
      trackExecution(ACTION_FLOW_EVENTS.COMPLETED, {
        flowLabel: actionFlow?.label,
        chainId: actionFlow?.chainId,
        status: lastStatus,
        txHash: lastHash,
        ...(lastStatus === 'reverted' && {
          errorCategory: 'transaction_reverted_onchain',
          stepIndex: activeStepIndex
        }),
        ...(revertedErrorName && {
          revertErrorName: revertedErrorName
        })
      });
    } catch (e) {
      const error_0 = ensureError(e);
      if (process.env.NODE_ENV === 'development') {
        console.error('[ActionFlow] Execution error:', error_0);
      }
      const submittedThisStep = hasSubmittedActiveStep && !(error_0 instanceof BatchTxBundleError && error_0.notIncluded);

      // Non-reportable ActionFlowErrors are silent flow-control (e.g. "Execution aborted"
      // raised by the abort race in executeStep). reset()/unmount have already transitioned
      // state, so we deliberately skip ERROR dispatch — otherwise the reducer would
      // overwrite the idle state with `errorMessage: 'Execution aborted'`, causing the
      // next dialog open to surface a stale cancel as a real error.
      // Reportable ones still dispatch + report; `error.errorType` drives labeling so
      // each throw site picks its own observability tag instead of being hardcoded here.
      if (error_0 instanceof ActionFlowError) {
        if (!error_0.reportable) {
          const category = classifyTransactionError(error_0.message);
          const isSafeProposalOutcome = error_0.errorType === 'safe-proposal-dropped' || error_0.errorType === 'safe-proposal-queued';
          if (category === 'safe_wallet_expired' || category === 'safe_wallet_replaced' || isSafeProposalOutcome) {
            dispatchUnlessAborted({
              type: 'ERROR',
              errorMessage: extractErrorMessage(error_0),
              isRetryBlocked: error_0.errorType === 'safe-proposal-queued' || category === 'safe_wallet_expired' && submittedThisStep
            });
            flowLogger?.withMetadata({
              stepIndex: activeStepIndex,
              stepLabel: activeStep?.label,
              errorType: error_0.errorType,
              cause: error_0.cause instanceof Error ? error_0.cause.message : undefined
            }).warn(ACTION_FLOW_LOG_EVENTS.ERROR);
            if (error_0.errorType === 'safe-proposal-queued') return;
            onStepSettled?.({
              submissionId,
              stepIndex: activeStepIndex,
              status: 'failed',
              error: error_0
            });
            trackExecution(error_0.errorType === 'safe-proposal-dropped' ? ACTION_FLOW_EVENTS.SAFE_PROPOSAL_DROPPED : category === 'safe_wallet_expired' ? ACTION_FLOW_EVENTS.SAFE_EXPIRED : ACTION_FLOW_EVENTS.SAFE_REPLACED, {
              flowLabel: actionFlow?.label,
              chainId: actionFlow?.chainId,
              stepIndex: activeStepIndex,
              stepLabel: activeStep?.label
            });
          }
          return;
        }
        dispatchUnlessAborted({
          type: 'ERROR',
          errorMessage: extractErrorMessage(error_0),
          isRetryBlocked: error_0.errorType === 'indeterminate-submission' || submittedThisStep
        });
        const errorCategory = classifyTransactionError(error_0.message);
        onStepSettled?.({
          submissionId,
          stepIndex: activeStepIndex,
          status: 'failed',
          error: error_0
        });

        // Neither is an app defect: the tx is likely still in flight, or the user is on
        // the wrong network and told how to fix it. Warn so alerts stay meaningful.
        const isExpectedFailure = errorCategory === 'timeout_receipt_polling' || error_0.errorType === 'chain-switch-error';
        const actionFlowErrorMetadata = {
          submissionId,
          walletMetadata,
          trackingContext: executionTrackingContext,
          walletAddress: walletClient?.account?.address,
          flowLabel: actionFlow?.label,
          flowTag: actionFlow?.tag,
          chainId: actionFlow?.chainId,
          flowState: state.flowState,
          step: activeStep,
          stepIndex: activeStepIndex,
          error: error_0,
          errorType: error_0.errorType,
          errorCategory
        } satisfies BuildActionFlowErrorMetadataParams;
        reportActionFlowError({
          flowLogger,
          errorReporter,
          error: error_0,
          metadata: buildActionFlowErrorMetadata(actionFlowErrorMetadata),
          severity: isExpectedFailure ? 'warn' : 'error',
          event: ACTION_FLOW_LOG_EVENTS.ERROR
        });
        trackExecution(ACTION_FLOW_EVENTS.ERROR, {
          errorType: error_0.errorType,
          errorCategory,
          flowLabel: actionFlow?.label,
          chainId: actionFlow?.chainId,
          flowState: state.flowState,
          stepIndex: activeStepIndex,
          stepLabel: activeStep?.label,
          stepType: activeStep?.type,
          phase,
          txHash: undefined,
          error_message: error_0.message
        });
        return;
      }
      const errorCategory_0 = classifyTransactionError(error_0.message);
      const isUserRejected = errorCategory_0 === 'user_rejected';

      // EIP-5792 batchTx errors carry debug metadata (bundle id + elapsed
      // time from sendCalls to failure — MKT-675). Forward to every
      // reporting surface so we can disambiguate wallet bugs from
      // propagation races in BetterStack/Sentry/PostHog.
      const debug = error_0 instanceof BatchTxBundleError ? error_0.debug : undefined;
      onStepSettled?.({
        submissionId,
        stepIndex: activeStepIndex,
        status: 'failed',
        error: error_0
      });

      // A rejection after submission is still reported because the transaction may land.
      // Pre-submission user rejections are expected user actions, not errors.
      if (!isUserRejected || submittedThisStep) {
        const isReceiptPollingTimeout = errorCategory_0 === 'timeout_receipt_polling';
        const executionErrorMetadata = {
          submissionId,
          walletMetadata,
          trackingContext: executionTrackingContext,
          walletAddress: walletClient?.account?.address,
          flowLabel: actionFlow?.label,
          flowTag: actionFlow?.tag,
          chainId: actionFlow?.chainId,
          flowState: state.flowState,
          step: activeStep,
          stepIndex: activeStepIndex,
          error: error_0,
          errorType: 'execution-error',
          errorCategory: errorCategory_0,
          debug
        } satisfies BuildActionFlowErrorMetadataParams;
        reportActionFlowError({
          flowLogger,
          errorReporter,
          error: error_0,
          metadata: buildActionFlowErrorMetadata(executionErrorMetadata),
          severity: isReceiptPollingTimeout ? 'warn' : 'error',
          event: ACTION_FLOW_LOG_EVENTS.ERROR
        });
        trackExecution(ACTION_FLOW_EVENTS.ERROR, {
          errorType: 'execution-error',
          errorCategory: errorCategory_0,
          flowLabel: actionFlow?.label,
          chainId: actionFlow?.chainId,
          flowState: state.flowState,
          stepIndex: activeStepIndex,
          stepLabel: activeStep?.label,
          stepType: activeStep?.type,
          phase,
          error_message: error_0.message,
          ...(debug && {
            debug
          })
        });
      }
      if (isUserRejected && !submittedThisStep) {
        dispatchUnlessAborted({
          type: 'USER_REJECTED'
        });
        flowLogger?.withMetadata({
          stepIndex: activeStepIndex,
          stepLabel: activeStep?.label
        }).info(ACTION_FLOW_LOG_EVENTS.USER_REJECTED);
        trackExecution(ACTION_FLOW_EVENTS.USER_REJECTED, {
          flowLabel: actionFlow?.label,
          chainId: actionFlow?.chainId,
          stepIndex: activeStepIndex,
          stepLabel: activeStep?.label,
          stepType: activeStep?.type,
          phase
        });
      } else {
        dispatchUnlessAborted({
          type: 'ERROR',
          errorMessage: extractErrorMessage(error_0),
          isRetryBlocked: submittedThisStep
        });
      }
    } finally {
      // Release the execution mutex, unless reset() already released it and a newer execution
      // owns it — this execution may have been awaiting `onSuccess` all the while.
      if (executeRef.current === executeState) executeRef.current = null;
      try {
        await onComplete?.();
      } catch {
        // Best-effort: onComplete is observability/cleanup — swallow failures
        // to avoid unhandled rejections after flow state has already transitioned
      }
    }
  }, [state.flowState, state.stepIndex, isRetryBlocked, walletClient, publicClient, ensureCorrectChain, steps, actionFlow, errorReporter, logger, onComplete, onStepSettled, track, reportAttributionIssue, isLoading, trackingContextRef, walletMetadata, walletSupportsAtomicBatching, interTransactionDelayMs, gasPolicy]);
  const setActionFlowWithTracking = useCallback((flow: ActionFlow | null) => {
    setActionFlow(flow);
    if (!flow) dispatch({
      type: 'CLEAR_FLOW'
    });
  }, []);
  const setTrackingContext = useCallback((ctx: Record<string, unknown>) => {
    trackingContextRef.current = {
      ...baseTrackingContext,
      ...ctx
    };
  }, [trackingContextRef, baseTrackingContext]);

  /**
   * Stops the running execution before its next wallet request while keeping the flow state
   * rendered. The reducer stays in `execution` with no runner, so callers must follow with `reset()`.
   */
  const abort = useCallback(() => {
    executeRef.current?.controller.abort();
  }, []);
  const reset = useCallback(() => {
    abort();
    // Released here rather than in the execution's `finally`: an aborted execution can still be
    // awaiting `onSuccess` (indexer sync + refetches), and holding the mutex until that settles
    // would silently reject the next action for the whole of it. The `finally` is
    // identity-guarded, so the abandoned execution cannot clear a newer one's lock.
    executeRef.current = null;
    // Owned here rather than by the abandoned execution's cleanup, which no longer writes it.
    setIsCallbackInFlight(false);
    setActionFlow(null);
    dispatch({
      type: 'RESET'
    });
  }, [abort]);

  // Abort execution on unmount
  useEffect(() => abort, [abort]);
  return {
    ...actionFlow,
    isLoading,
    isIdle,
    isExecution,
    isSuccess,
    isReverted,
    hasSteps,
    txHash,
    /**
     * True while a `before` / `after` callback is awaiting. Consumed by
     * `ActionFlowSteps` to swap "Proceed in your wallet" / "Finalizing…"
     * copy for "Waiting…" when a callback (not a wallet / chain action) is
     * what's actually in flight.
     */
    isCallbackInFlight,
    state,
    stepLabels: steps.map(step_1 => step_1.label),
    setActionFlow: setActionFlowWithTracking,
    setTrackingContext,
    execute,
    abort,
    reset,
    reportRenderError: errorReporter ? reportRenderError : undefined
  };
}

/**
 * Provides action flow execution state to its children.
 *
 * All observability props are optional and additive:
 * - `errorReporter` — captures exceptions to Sentry (+ wraps with ErrorBoundary)
 * - `eventTracker` — emits core analytics events to PostHog
 * - `logger` — emits structured lifecycle logs (10 events) for developer debugging.
 *   Reportable execution errors also use the logger as their single reporting sink
 *   when an error-reporter plugin is attached (see `createClientLogger`).
 *   On-chain reverts are completed outcomes (`action_flow.completed`, status `reverted`);
 *   wallet/bundle/execution failures use `action_flow.error`.
 * - `trackingContext` — merged into every analytics event and log entry
 * - `interTransactionDelayMs` — pause between sequential tx steps without an explicit
 *   `after` / `before` wait, for non-batching wallets; defaults to 750ms.
 * - `onComplete` — called after every execution (success, revert, or error).
 *   Use for flushing observability buffers or other post-execution cleanup.
 */
export function ActionFlowProvider(t0) {
  const $ = _c(22);
  const {
    children,
    errorReporter,
    eventTracker,
    logger,
    onComplete,
    onStepSettled,
    interTransactionDelayMs: t1,
    gasPolicy,
    trackingContext
  } = t0;
  const interTransactionDelayMs = t1 === undefined ? DEFAULT_INTER_TRANSACTION_DELAY_MS : t1;
  let t2;
  if ($[0] !== trackingContext) {
    t2 = trackingContext ?? {};
    $[0] = trackingContext;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const trackingContextRef = useRef(t2);
  let t3;
  let t4;
  if ($[2] !== trackingContext) {
    t3 = () => {
      trackingContextRef.current = trackingContext ?? {};
    };
    t4 = [trackingContext];
    $[2] = trackingContext;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  const walletMetadata = useActionFlowWalletMetadata();
  let t5;
  if ($[5] !== errorReporter || $[6] !== eventTracker || $[7] !== gasPolicy || $[8] !== interTransactionDelayMs || $[9] !== logger || $[10] !== onComplete || $[11] !== onStepSettled || $[12] !== trackingContext || $[13] !== walletMetadata) {
    t5 = {
      errorReporter,
      eventTracker,
      logger,
      onComplete,
      onStepSettled,
      interTransactionDelayMs,
      gasPolicy,
      trackingContextRef,
      walletMetadata,
      baseTrackingContext: trackingContext
    };
    $[5] = errorReporter;
    $[6] = eventTracker;
    $[7] = gasPolicy;
    $[8] = interTransactionDelayMs;
    $[9] = logger;
    $[10] = onComplete;
    $[11] = onStepSettled;
    $[12] = trackingContext;
    $[13] = walletMetadata;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const hook = useActionFlowHook(t5);
  let t6;
  if ($[15] !== children || $[16] !== hook) {
    t6 = <ActionFlowContext.Provider value={hook}>{children}</ActionFlowContext.Provider>;
    $[15] = children;
    $[16] = hook;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const provider = t6;
  if (hook.reportRenderError) {
    let t7;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="text-destructive-foreground p-4">Something went wrong with this transaction. Please refresh and try again.</div>;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    let t8;
    if ($[19] !== hook.reportRenderError || $[20] !== provider) {
      t8 = <ErrorBoundary onError={hook.reportRenderError} fallback={t7}>{provider}</ErrorBoundary>;
      $[19] = hook.reportRenderError;
      $[20] = provider;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    return t8;
  }
  return provider;
}
export const useActionFlow = () => {
  return useMandatoryContext(ActionFlowContext, "ActionFlow");
};

/**
 * Report a caught, reportable ActionFlow execution error to a single sink.
 *
 * When a `flowLogger` is available, the error is reported once via
 * `withError(error).withMetadata(metadata)` at the given severity using the
 * the provided event. `action_flow.error` is for execution failures; on-chain
 * reverts report through `action_flow.completed` with `status: 'reverted'` so
 * BetterStack and PostHog classify the same outcome the same way. The logger's
 * error-reporter plugin still captures the revert exception from that log call
 * (see `createClientLogger`), and direct `errorReporter.captureException` is
 * only the fallback for a logger-less consumer.
 *
 * PostHog terminal-event emission stays with the caller: analytics events
 * (and transaction-revert deduplication) are handled independently of this
 * reporting sink.
 */
function reportActionFlowError({
  flowLogger,
  errorReporter,
  error,
  metadata,
  severity,
  event
}: {
  flowLogger: ILogLayer | undefined;
  errorReporter: ErrorReporter | undefined;
  error: Error;
  metadata: ActionFlowErrorMetadata & {
    status?: 'reverted';
  };
  severity: 'error' | 'warn';
  event: typeof ACTION_FLOW_LOG_EVENTS.ERROR | typeof ACTION_FLOW_LOG_EVENTS.COMPLETED;
}) {
  if (flowLogger) {
    const entry = flowLogger.withError(error).withMetadata(metadata);
    if (severity === 'warn') entry.warn(event);else entry.error(event);
    return;
  }
  errorReporter?.captureException(error, metadata as unknown as Record<string, unknown>);
}

/**
 * Fetch the full viem receipt for a tx the engine just confirmed.
 *
 * `executeStep` returns an `ActionFlowReceipt` shape for observability
 * surfaces, but `before` / `after` callbacks want viem's
 * `TransactionReceipt` (block number, logs, etc.) on the `WaitContext`.
 *
 * On the `singleTx` path `executeStep` already blocked on
 * `waitForTransactionReceipt(publicClient, …)`, so publicClient definitely
 * has the receipt by the time we ask. On the `batchTx` (EIP-5792) path
 * `executeStep` confirmed via `waitForCallsStatus(walletClient, …)` —
 * publicClient may briefly lag the wallet's RPC because the wallet's RPC
 * was the broadcast origin. A one-shot `getTransactionReceipt` would race
 * and return `null` on that path, silently bypassing any `after` callback
 * that depends on the receipt (notably `waitForConfirmations`). We instead
 * `waitForTransactionReceipt`, which polls publicClient until the receipt
 * lands. The wait is bounded by viem's internal timeout.
 *
 * The wait is also raced against the engine's abort signal. Without
 * `raceAbort`, a reset/unmount triggered between `executeStep` returning
 * and viem timing out would leave the execution loop blocked on the
 * orphan poll — holding `executeRef` and stalling a prompt restart.
 * Returning null on abort preserves the existing contract: callers
 * already treat null as "fall back to the blanket inter-tx delay", and
 * the loop's `controller.signal.aborted` checks downstream short-circuit
 * before any further work.
 */
async function resolveViemReceipt(publicClient: Client, hash: Hex, signal: AbortSignal): Promise<TransactionReceipt | null> {
  if (signal.aborted) return null;
  try {
    return await raceAbort(waitForTransactionReceipt(publicClient, {
      hash
    }), signal);
  } catch {
    return null;
  }
}